import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { Category } from "@/mocks/admin";

function rowToCategory(row: Record<string, unknown>): Category {
  return {
    id: row.id as string,
    name: row.name as string,
    slug: row.slug as string,
    description: (row.description as string) || "",
    color: (row.color as string) || "#8B0000",
    imageUrl: row.image_url as string | undefined,
    articleCount: (row.article_count as number) || 0,
    isActive: row.is_active as boolean,
    order: (row.sort_order as number) || 0,
    showInMenu: row.show_in_menu as boolean,
    showOnHomepage: row.show_on_homepage as boolean,
    homepageOrder: (row.homepage_order as number) || 0,
    seoTitle: row.seo_title as string | undefined,
    seoDescription: row.seo_description as string | undefined,
    subcategories: (row.subcategories as Category["subcategories"]) || [],
  };
}

function categoryToRow(cat: Partial<Category>): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if (cat.name !== undefined) row.name = cat.name;
  if (cat.slug !== undefined) row.slug = cat.slug;
  if (cat.description !== undefined) row.description = cat.description;
  if (cat.color !== undefined) row.color = cat.color;
  if (cat.imageUrl !== undefined) row.image_url = cat.imageUrl;
  if (cat.articleCount !== undefined) row.article_count = cat.articleCount;
  if (cat.isActive !== undefined) row.is_active = cat.isActive;
  if (cat.order !== undefined) row.sort_order = cat.order;
  if (cat.showInMenu !== undefined) row.show_in_menu = cat.showInMenu;
  if (cat.showOnHomepage !== undefined) row.show_on_homepage = cat.showOnHomepage;
  if (cat.homepageOrder !== undefined) row.homepage_order = cat.homepageOrder;
  if (cat.seoTitle !== undefined) row.seo_title = cat.seoTitle;
  if (cat.seoDescription !== undefined) row.seo_description = cat.seoDescription;
  if (cat.subcategories !== undefined) row.subcategories = cat.subcategories;
  return row;
}

export function useCategoriesDB() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error: dbError } = await supabase
      .from("categories")
      .select("*")
      .order("sort_order");

    if (dbError) {
      setError(dbError.message);
      setCategories([]);
    } else {
      setCategories((data ?? []).map((r) => rowToCategory(r as Record<string, unknown>)));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const createCategory = useCallback(async (cat: Omit<Category, "id">): Promise<{ data: Category | null; error: string | null }> => {
    const row = categoryToRow(cat);
    const { data, error: dbError } = await supabase
      .from("categories")
      .insert(row)
      .select()
      .maybeSingle();

    if (dbError) return { data: null, error: dbError.message };
    if (!data) return { data: null, error: "Unbekannter Fehler beim Erstellen." };

    const newCat = rowToCategory(data as Record<string, unknown>);
    await fetchCategories();
    return { data: newCat, error: null };
  }, [fetchCategories]);

  const updateCategory = useCallback(async (id: string, updates: Partial<Category>): Promise<{ error: string | null }> => {
    const row = categoryToRow(updates);
    const { error: dbError } = await supabase.from("categories").update(row).eq("id", id);
    if (dbError) return { error: dbError.message };
    await fetchCategories();
    return { error: null };
  }, [fetchCategories]);

  const deleteCategory = useCallback(async (id: string): Promise<{ error: string | null }> => {
    const { error: dbError } = await supabase.from("categories").delete().eq("id", id);
    if (dbError) return { error: dbError.message };
    await fetchCategories();
    return { error: null };
  }, [fetchCategories]);

  return { categories, loading, error, refetch: fetchCategories, createCategory, updateCategory, deleteCategory };
}
